import React, { Component, type ReactNode, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocation } from "wouter";
import { isBenignQueryCancellation, queryFailureLabelKey, queryFailureOriginLabelKey, readableQueryError } from "@/lib/queryErrorDisplay";

type FailedQuery = { hash: string; labelKey: string; originLabelKey: string; message: string };
type BoundaryProps = { children: ReactNode };
type BoundaryState = { hasError: boolean };

/** Query feedback must never take down the route it is meant to protect. */
export class GlobalQueryErrorNoticeBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { hasError: false };

  static getDerivedStateFromError(): BoundaryState {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return <aside className="fixed inset-x-3 bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] z-[80] mx-auto max-w-lg rounded-xl border border-red/40 bg-card p-3 text-sm text-foreground shadow-xl" role="alert">Unable to show query error status.</aside>;
    }
    return this.props.children;
  }
}

export function GlobalQueryErrorNotice() {
  const client = useQueryClient();
  const { t } = useLanguage();
  const [location] = useLocation();
  const [failed, setFailed] = useState<FailedQuery | null>(null);

  useEffect(() => client.getQueryCache().subscribe((event) => {
    if (event.type !== "updated" || event.action.type !== "error") return;
    const error = "error" in event.action ? event.action.error : undefined;
    if (isBenignQueryCancellation(error)) return;
    setFailed({ hash: event.query.queryHash, labelKey: queryFailureLabelKey(event.query.queryKey), originLabelKey: queryFailureOriginLabelKey(location), message: readableQueryError(error, t("route.retryDescription")) });
  }), [client, location, t]);

  useEffect(() => { setFailed(null); }, [location]);

  if (!failed) return null;

  return <aside className="fixed inset-x-3 bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] z-[80] mx-auto flex max-w-lg items-start gap-3 rounded-xl border border-red/40 bg-card p-3 shadow-xl" role="alert">
    <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red" />
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold">{t("route.errorTitle")}</p>
      <p className="mt-0.5 truncate text-xs text-muted-foreground">{t(failed.labelKey)}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{t("route.errorOrigin")}: {t(failed.originLabelKey)}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{failed.message}</p>
    </div>
    <Button size="sm" className="min-h-11 shrink-0" onClick={() => { void client.refetchQueries({ predicate: (query) => query.queryHash === failed.hash }); setFailed(null); }}>
      <RefreshCw className="h-4 w-4" />{t("route.retry")}
    </Button>
    <button type="button" aria-label={t("common.close")} className="min-h-11 min-w-11 text-muted-foreground" onClick={() => setFailed(null)}><X className="mx-auto h-4 w-4" /></button>
  </aside>;
}
